import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import styles from './BlockedDomainsTable.module.scss'

import { isApiError } from '@/api'
import { BlockedDomainItem } from '@/api/generated'
import { removeBlockedDomain } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { useToast } from '@/components/Toast/ToastContext'
import { BLOCKED_DOMAINS_QUERY_KEY } from '@/pages/app/BlockedDomains'

interface BlockedDomainsTableProps {
  domains: BlockedDomainItem[]
  isLoading: boolean
  onAdd: () => void
}

const BlockedDomainsTable = ({ domains, isLoading, onAdd }: BlockedDomainsTableProps) => {
  const [domainToDelete, setDomainToDelete] = useState<BlockedDomainItem | null>(null)
  const queryClient = useQueryClient()
  const { updateToast } = useToast()

  const deleteMutation = useMutation({
    mutationFn: (domainId: string) => removeBlockedDomain(domainId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BLOCKED_DOMAINS_QUERY_KEY })
      setDomainToDelete(null)
    },
    onError: (err) => {
      if (isApiError(err)) {
        updateToast(err.detail || 'Failed to delete blocked domain', 'error')
      } else {
        updateToast('Failed to delete blocked domain', 'error')
      }
    },
  })

  const handleDelete = () => {
    if (!domainToDelete) return
    deleteMutation.mutate(domainToDelete.id)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString()
  }

  if (isLoading) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.loadingState}>Loading blocked domains...</div>
      </div>
    )
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableControls}>
        <p className={styles.tableSubtitle}>
          Domains on this list are blocked from self-serve team creation. Domains are case-insensitive and stored as
          lowercase.
        </p>
        <Button title="+ Add" onClick={onAdd} type="dark" />
      </div>

      {domains.length === 0 ? (
        <div className={styles.emptyState}>No blocked domains yet. Click &apos;+ Add&apos; to create one.</div>
      ) : (
        <table className={styles.table} aria-label="Blocked domains">
          <thead className={styles.tableHeader}>
            <tr>
              <th scope="col" aria-label="Blocked domain name">
                Domain
              </th>
              <th scope="col" aria-label="Notes about why domain is blocked">
                Notes
              </th>
              <th scope="col" aria-label="Admin who blocked the domain">
                Created By
              </th>
              <th scope="col" aria-label="Date when domain was blocked">
                Created At
              </th>
              <th scope="col" aria-label="Delete actions">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {domains.map((domain) => (
              <tr key={domain.id}>
                <td>
                  <span className={styles.domainName}>{domain.domain}</span>
                </td>
                <td>
                  <span className={styles.truncatedText} title={domain.notes || undefined}>
                    {domain.notes || '-'}
                  </span>
                </td>
                <td>{domain.created_by}</td>
                <td>{formatDate(domain.created_at)}</td>
                <td>
                  <div className={styles.actionButtons}>
                    <Button type="destructive" onClick={() => setDomainToDelete(domain)} title="Delete" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <DialogWrapper
        isOpen={!!domainToDelete}
        title="Delete Blocked Domain"
        close={() => setDomainToDelete(null)}
        minWidth={400}
      >
        <div className={styles.dialogBody}>
          <p>
            Are you sure you want to remove <strong>{domainToDelete?.domain}</strong> from the block list?
          </p>
          <div className={styles.dialogFooter}>
            <Button title="Cancel" onClick={() => setDomainToDelete(null)} type="light" />
            <Button
              title={deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              onClick={handleDelete}
              type="destructive"
              isDisabled={deleteMutation.isPending}
            />
          </div>
        </div>
      </DialogWrapper>
    </div>
  )
}

export default BlockedDomainsTable
